import { PrivacyBlur } from "#components/privacy-blur"
import { useTheme } from "#components/theme-provider"
import { Card, CardContent, CardTitle } from "#components/ui/card"
import { StatLabel, StatValue } from "#components/ui/typography"
import { useCurrencyFormatters } from "#hooks/use-currency-formatters"
import { useI18n } from "#hooks/use-i18n"
import { useThemeColors } from "#hooks/use-theme-colors"
import { cardEntrance, getCardHoverEffect, statValue } from "#lib/animations"
import type { DashboardAnalyticsResponse } from "@home-finance-manager/shared/types/dashboard"
import { motion } from "framer-motion"
import { TrendingDown, TrendingUp } from "lucide-react"
import React from "react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

interface PartnerContributionCardProps {
  analytics: DashboardAnalyticsResponse | undefined
}

export const PartnerContributionCard = React.memo<PartnerContributionCardProps>(({ analytics }) => {
  const { t } = useI18n()
  const { formatCurrency } = useCurrencyFormatters()
  const themeColors = useThemeColors()
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === "dark"

  // Get the most recent month's data (last in the array, as they're sorted chronologically)
  const selectedMonthIndex = React.useMemo(() => {
    if (
      !analytics?.partnerContributionsByMonth ||
      analytics.partnerContributionsByMonth.length === 0
    )
      return -1
    return analytics.partnerContributionsByMonth.length - 1
  }, [analytics])

  const selectedMonthData =
    selectedMonthIndex >= 0
      ? analytics?.partnerContributionsByMonth?.[selectedMonthIndex] || null
      : null
  const partnerContributions = selectedMonthData?.partnerContributions || []

  // Partner colors for indicators
  const partnerColors = ["#3b82f6", "#8b5cf6", "#ef4444", "#f59e0b", "#22c55e"]

  // Prepare chart data
  const chartData = partnerContributions.map((contribution, index) => {
    const extraIncome = contribution.extraIncome || 0
    const totalIncome = contribution.netMonthlySalary + extraIncome
    return {
      name: contribution.partnerName,
      spending: contribution.totalSpending,
      income: totalIncome,
      ratio: contribution.spendingToIncomeRatio,
      contributionPercentage: contribution.contributionPercentage,
      partnerColor: partnerColors[index % partnerColors.length],
      extraIncome,
    }
  })

  // Calculate total spending
  const totalSpending = partnerContributions.reduce((sum, p) => sum + p.totalSpending, 0)

  // Get bar color based on spending-to-income ratio
  const getBarColor = (ratio: number) => {
    if (ratio < 0.5) return "#22c55e" // green
    if (ratio < 0.8) return "#f59e0b" // yellow
    return "#ef4444" // red
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={cardEntrance}
      whileHover={getCardHoverEffect(isDark)}
      className="h-full min-h-0 flex flex-col"
    >
      <Card className="flex flex-col overflow-hidden h-full max-h-full border-border/50 bg-card/50 backdrop-blur-sm shadow-sm dark:shadow-none dark:border-border/50 transition-shadow hover:shadow-md dark:hover:shadow-none">
        {/* Accent border */}
        <div
          className={`h-1 absolute top-0 left-0 right-0 ${
            isDark
              ? "bg-gradient-to-r from-primary/60 via-primary/40 to-transparent"
              : "bg-gradient-to-r from-primary/80 via-primary/60 to-transparent"
          }`}
        />
        <CardContent className="p-6 flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="mb-4 flex-shrink-0">
            <CardTitle className="text-base font-medium mb-3 text-foreground">
              {t("components.dashboard.partnerContribution.title")}
            </CardTitle>
            <motion.div variants={statValue}>
              <StatValue className="text-3xl sm:text-4xl mb-2">
                {formatCurrency(totalSpending)}
              </StatValue>
            </motion.div>
            <StatLabel>
              {t("components.dashboard.partnerContribution.totalSpendingThisMonth")}
            </StatLabel>
          </div>

          {/* Chart */}
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {chartData.length > 0 ? (
              <div className="h-48 sm:h-56 flex-shrink-0 w-full">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <BarChart data={chartData} margin={{ top: 10, right: 0, left: 0, bottom: 10 }}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}
                    />
                    <XAxis
                      dataKey="name"
                      angle={0}
                      textAnchor="middle"
                      height={10}
                      tick={{
                        fill: themeColors.cardForeground,
                        fontSize: 10,
                      }}
                      interval={0}
                    />
                    <YAxis
                      width={50}
                      tick={{
                        fill: themeColors.cardForeground,
                        fontSize: 11,
                      }}
                      tickFormatter={(value) => {
                        // Format large numbers more compactly
                        if (value >= 1000) {
                          return `${(value / 1000).toFixed(0)}k €`
                        }
                        return formatCurrency(value)
                      }}
                    />
                    <Tooltip
                      cursor={false}
                      content={({ active, payload, label }) => {
                        if (!active || !payload || !payload.length) return null

                        const spendingEntry = payload.find((p: any) => p.dataKey === "spending")
                        const incomeEntry = payload.find((p: any) => p.dataKey === "income")

                        return (
                          <div
                            className="rounded-md border bg-card px-3 py-2 text-sm shadow-md"
                            style={{
                              backgroundColor: themeColors.card,
                              borderColor: themeColors.border,
                              color: themeColors.cardForeground,
                            }}
                          >
                            <div className="font-medium mb-2">{label}</div>
                            {spendingEntry && (
                              <div className="flex items-center gap-2 mb-1">
                                <TrendingDown className="w-4 h-4 flex-shrink-0 text-destructive" />
                                <span>
                                  <PrivacyBlur>
                                    {formatCurrency(spendingEntry.value as number)}
                                  </PrivacyBlur>
                                </span>
                              </div>
                            )}
                            {incomeEntry && (
                              <div className="flex items-center gap-2">
                                <TrendingUp className="w-4 h-4 flex-shrink-0 text-green-600" />
                                <span>
                                  <PrivacyBlur>
                                    {formatCurrency(incomeEntry.value as number)}
                                  </PrivacyBlur>
                                </span>
                              </div>
                            )}
                          </div>
                        )
                      }}
                      wrapperStyle={{
                        outline: "none",
                      }}
                    />
                    <Bar
                      dataKey="spending"
                      radius={[4, 4, 0, 0]}
                      activeBar={{
                        opacity: 0.7,
                        strokeWidth: 1,
                      }}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getBarColor(entry.ratio)} />
                      ))}
                    </Bar>
                    <Bar
                      dataKey="income"
                      fill="#94a3b8"
                      radius={[4, 4, 0, 0]}
                      opacity={0.5}
                      activeBar={{
                        opacity: 0.5,
                        strokeWidth: 1,
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-sm text-muted-foreground text-center">
                  {t("components.dashboard.partnerContribution.noContributionsAvailable")}
                </div>
              </div>
            )}

            {/* Partner Details List */}
            {partnerContributions.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border/50 flex-1 min-h-0 overflow-y-auto space-y-0 pr-1">
                {partnerContributions.map((contribution, index) => (
                  <div
                    key={contribution.partnerId}
                    className="relative flex items-center justify-between text-sm group hover:bg-muted/30 rounded-md px-3 py-2 transition-colors border border-transparent hover:border-border/50"
                  >
                    {index < partnerContributions.length - 1 && (
                      <div
                        className="absolute bottom-0 left-0 right-0 h-px pointer-events-none"
                        style={{
                          background: `linear-gradient(to right, transparent 0%, var(--border) 20%, var(--border) 80%, transparent 100%)`,
                          opacity: 0.5,
                        }}
                      />
                    )}
                    <div className="flex items-center gap-2 min-w-0">
                      <div
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor:
                            partnerColors[
                              partnerContributions.findIndex(
                                (p) => p.partnerId === contribution.partnerId
                              ) % partnerColors.length
                            ],
                        }}
                      />
                      <span className="text-foreground font-medium truncate">
                        {contribution.partnerName}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                      <span className="text-muted-foreground text-xs">
                        {(contribution.equitablePercentage || 0).toFixed(1)}%
                      </span>
                      <span className="font-semibold text-foreground min-w-[4rem] text-right">
                        <PrivacyBlur>{formatCurrency(contribution.totalSpending)}</PrivacyBlur>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
})

PartnerContributionCard.displayName = "PartnerContributionCard"
